import { useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { FilterMatchMode, FilterService } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { formatMinutes } from "sj-ui-toolkit/src/utils/time";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { skeletonRows, withSkeleton } from "@/helpers/tableBody";
import { multiSelectOnChangeFilterElement, type SelectOption } from "@/helpers/tableFilters";
import TrainingLessonsSidebar from "./TrainingLessonsSidebar";
import type { TrainingPlaAssignment } from "./types";

// OR-semantics column filter for "CWEs Included": show rows whose detected-CWE
// list includes any of the selected CWEs. The multiselect supplies string values
// and the row stores numbers, so bridge the two with Number().
FilterService.register("cwesContainsAny", (value: unknown, filter: unknown) => {
  if (!Array.isArray(filter) || filter.length === 0) return true;
  if (!Array.isArray(value)) return false;
  return (filter as string[]).some(selected => (value as number[]).includes(Number(selected)));
});

const EMPTY_FILTERS = {
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  displayName: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  cwesIncluded: {
    value: null as string[] | null,
    matchMode: "cwesContainsAny" as FilterMatchMode,
  },
  activePlaDisplay: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
};

const GLOBAL_FILTER_FIELDS = [
  "displayName",
  "email",
  "givenName",
  "familyName",
  "attributes.department",
  "activePlaDisplay",
];

const CONFIGURABLE_COLUMNS = [
  {
    field: "attributes.employeeNumber",
    header: "Employee #",
    sortable: false,
    visibleInitially: false,
  },
  { field: "attributes.jobRole", header: "Job Role", sortable: true, visibleInitially: false },
  { field: "attributes.title", header: "Title", sortable: true, visibleInitially: false },
  { field: "attributes.country", header: "Country", sortable: true, visibleInitially: false },
  { field: "attributes.company", header: "Company", sortable: true, visibleInitially: false },
  {
    field: "attributes.businessUnit",
    header: "Business Unit",
    sortable: true,
    visibleInitially: false,
  },
  { field: "attributes.teamName", header: "Team Name", sortable: true, visibleInitially: false },
  {
    field: "attributes.managersEmail",
    header: "Manager's Email",
    sortable: true,
    visibleInitially: false,
  },
  { field: "attributes.managersName", header: "Manager", sortable: true, visibleInitially: false },
  { field: "attributes.department", header: "Department", sortable: true, visibleInitially: false },
] as const;

const assignmentBodyTemplate = withSkeleton<TrainingPlaAssignment>(row => (
  <div>
    <div className="font-semibold">{row.displayName}</div>
    <div className={`body-sm ${themeClasses.textSecondary}`}>{row.email}</div>
  </div>
));

const lastCweBodyTemplate = withSkeleton<TrainingPlaAssignment>(row =>
  row.lastCweDetection ? humanDateWithoutTime(row.lastCweDetection) : "--"
);

// `cwesIncluded` is the deduped, sorted list of CWEs detected in the learner's
// code scans (computed in transformPlaData). Same data behind "Last CWE Detection".
const cwesIncludedBodyTemplate = withSkeleton<TrainingPlaAssignment>(row =>
  row.cwesIncluded.length > 0 ? row.cwesIncluded.join(", ") : "--"
);

interface TrainingAvailableAssignmentsProps {
  assignments?: TrainingPlaAssignment[];
  selectedAssignments?: TrainingPlaAssignment[];
  onSelectionChange: (
    selection:
      | TrainingPlaAssignment[]
      | ((previous: TrainingPlaAssignment[]) => TrainingPlaAssignment[])
  ) => void;
  isLoading?: boolean;
  showValidationMessages?: boolean;
  validationErrors?: { hasError?: boolean; message?: string };
  lessonSelections?: Record<string, string[]>;
  onLessonSave: (plaUuid: string, lessonKeys: string[]) => void;
  onLessonSaveAndInclude: (plaUuid: string, lessonKeys: string[]) => void;
}

export default function TrainingAvailableAssignments({
  assignments = [],
  selectedAssignments = [],
  onSelectionChange,
  isLoading = false,
  showValidationMessages = false,
  validationErrors,
  lessonSelections = {},
  onLessonSave,
  onLessonSaveAndInclude,
}: TrainingAvailableAssignmentsProps) {
  const dt = useRef<DataTable<DataTableValueArray>>(null);
  const stateKey = `training-available-assignments-${getCurrentSelectedTenant() ?? ""}`;
  const [sidebarAssignment, setSidebarAssignment] = useState<TrainingPlaAssignment | null>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [visibleColumns, setVisibleColumns] = useState(
    CONFIGURABLE_COLUMNS.filter(column => column.visibleInitially)
  );

  const storedRows = useMemo(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { rows?: number };
        if (parsed.rows) return parsed.rows;
      }
    } catch {
      // ignore
    }
    return DEFAULT_ROW_COUNT;
  }, []);

  const skeletonItems = useMemo(
    () => skeletonRows<TrainingPlaAssignment>(storedRows),
    [storedRows]
  );

  const selectedLessonsMinutes = (rowData: TrainingPlaAssignment) => {
    const selectedKeys = lessonSelections[rowData.uuid] ?? rowData.defaultSelectedLessonKeys ?? [];
    const durationsByKey = new Map(
      (rowData._original.lessons || []).map(lesson => [lesson.lessonKey, lesson.duration] as const)
    );
    return selectedKeys.reduce((sum, key) => sum + (Number(durationsByKey.get(key) || 0) || 0), 0);
  };

  const lessonsBody = useMemo(
    () =>
      withSkeleton<TrainingPlaAssignment>(row => {
        const selectedCount =
          lessonSelections[row.uuid]?.length ??
          row.defaultSelectedLessonKeys?.length ??
          row.lessonsTotal;
        return (
          <Button
            text
            className="p-0"
            onClick={() => setSidebarAssignment(row)}
            aria-label={`${selectedCount} of ${row.lessonsTotal} lessons selected. Click to edit.`}
          >
            {selectedCount} of {row.lessonsTotal} selected
          </Button>
        );
      }),
    [lessonSelections]
  );

  const durationBody = useMemo(
    () =>
      withSkeleton<TrainingPlaAssignment>(
        row => formatMinutes(selectedLessonsMinutes(row)),
        "6rem"
      ),
    [lessonSelections]
  );

  const cweFilterOptions = useMemo<SelectOption[]>(() => {
    const cweNumbers = new Set<number>();
    assignments.forEach(assignment => assignment.cwesIncluded.forEach(cwe => cweNumbers.add(cwe)));
    return [...cweNumbers]
      .sort((left, right) => left - right)
      .map(cwe => ({ label: `CWE-${cwe}`, value: String(cwe) }));
  }, [assignments]);

  // Applies the selection to filter state directly (via multiSelectOnChangeFilterElement)
  // rather than PrimeReact's filterApplyCallback, which fires with stale filter state.
  const cweFilterElement = multiSelectOnChangeFilterElement(
    cweFilterOptions,
    "Filter by CWE",
    value =>
      setFilters(previous => ({
        ...previous,
        cwesIncluded: { value, matchMode: "cwesContainsAny" as FilterMatchMode },
      }))
  );

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters(previous => ({ ...previous, global: { ...previous.global, value } }));
    setGlobalFilterValue(value);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setGlobalFilterValue("");
  };

  const onColumnSelectChange = (event: { value: string[] }) => {
    setVisibleColumns(CONFIGURABLE_COLUMNS.filter(column => event.value.includes(column.field)));
  };

  return (
    <>
      <h3 className="mb-2">Available Assignments</h3>
      <p className="mb-4 body-sm font-semibold uppercase text-primary">
        {selectedAssignments.length} assignments selected
      </p>

      <Card className={themeClasses.bgElevation1}>
        <div className="mb-4 flex flex-wrap justify-between gap-4">
          <div className="flex items-center gap-4">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Search"
                aria-label="Search available assignments"
                autoComplete="off"
              />
            </IconField>
            <Button
              label="Clear"
              icon="pi pi-filter-slash"
              size="small"
              severity="secondary"
              className={globalFilterValue ? "" : "hidden"}
              onClick={clearFilters}
            />
          </div>

          <div className="flex items-center gap-4">
            <MultiSelect
              value={visibleColumns.map(column => column.field)}
              onChange={onColumnSelectChange}
              options={[...CONFIGURABLE_COLUMNS]}
              optionLabel="header"
              optionValue="field"
              placeholder="Select Columns"
              aria-label="Select visible columns"
              className="w-48"
              maxSelectedLabels={2}
            />
            <Button
              label="Export data"
              icon="pi pi-download"
              iconPos="left"
              size="small"
              disabled={isLoading}
              onClick={() => dt.current?.exportCSV({ selectionOnly: false })}
            />
          </div>
        </div>

        <DataTable
          ref={dt}
          aria-label="Available personalized lesson assignments"
          value={(isLoading ? skeletonItems : assignments) as unknown as DataTableValueArray}
          // Skeleton rows are `null`, so a "uuid" dataKey resolves them all to the
          // same React key and the stale skeleton DOM survives the load. Key by
          // index while loading, then by uuid once real rows are present.
          dataKey={isLoading ? undefined : "uuid"}
          stateKey={stateKey}
          stateStorage="local"
          paginator
          selectionMode="multiple"
          selection={selectedAssignments}
          onSelectionChange={event =>
            onSelectionChange(
              (event.value as Array<TrainingPlaAssignment | null>).filter(
                (item): item is TrainingPlaAssignment => item != null
              )
            )
          }
          metaKeySelection={false}
          filters={isLoading ? undefined : filters}
          onFilter={event =>
            setFilters({ ...EMPTY_FILTERS, ...(event.filters as typeof EMPTY_FILTERS) })
          }
          filterDisplay="menu"
          globalFilterFields={isLoading ? [] : GLOBAL_FILTER_FIELDS}
          rows={storedRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          removableSort
          tableClassName="dt-responsive-text"
          emptyMessage="No available personalized lesson assignments"
          onStateSave={(state: Record<string, unknown>) => {
            const cleaned = { ...state };
            delete cleaned.sortField;
            delete cleaned.sortOrder;
            delete cleaned.selection;
            localStorage.setItem(stateKey, JSON.stringify(cleaned));
          }}
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
          <Column
            field="displayName"
            header="Assignment"
            sortable
            filter
            filterPlaceholder="Search by name or email"
            body={assignmentBodyTemplate}
          />
          <Column field="lessonsTotal" header="Lessons" sortable body={lessonsBody} />
          <Column
            field="cwesIncluded"
            header="CWEs Included"
            sortable={false}
            body={cwesIncludedBodyTemplate}
            filter
            filterElement={cweFilterElement}
            filterMatchMode={"cwesContainsAny" as FilterMatchMode}
            showFilterMatchModes={false}
          />
          <Column
            field="selectedLessonsDuration"
            header="Duration"
            sortable={false}
            body={durationBody}
          />
          <Column field="activePlaDisplay" header="Active PLAs" sortable filter />
          <Column
            field="lastCweDetection"
            header="Last CWE Detection"
            sortable
            body={lastCweBodyTemplate}
          />
          {CONFIGURABLE_COLUMNS.filter(column =>
            visibleColumns.some(visibleColumn => visibleColumn.field === column.field)
          ).map(column => (
            <Column
              key={column.field}
              field={column.field}
              header={column.header}
              sortable={column.sortable}
            />
          ))}
        </DataTable>
      </Card>

      {showValidationMessages && validationErrors?.hasError && (
        <div className="mt-4">
          <ValidationMessage severity="error" text={validationErrors.message ?? ""} />
        </div>
      )}

      <TrainingLessonsSidebar
        visible={sidebarAssignment !== null}
        onHide={() => setSidebarAssignment(null)}
        assignment={sidebarAssignment}
        selectedLessonKeys={sidebarAssignment ? lessonSelections[sidebarAssignment.uuid] || [] : []}
        onSave={onLessonSave}
        onSaveAndInclude={onLessonSaveAndInclude}
      />
    </>
  );
}
